import { ReactNode } from "react";

import { Button, Card, Icon } from "@stellar/design-system";

export type SettingsBannerAction = {
  label: string;
  onClick: () => void;
  variant?: "primary" | "secondary" | "tertiary";
};

type SettingsBannerProps = {
  variant: "warning" | "error";
  title: string;
  /** Body copy, rendered exactly like a Settings card's note. */
  children: ReactNode;
  /** Buttons in the title row, where a Settings card puts its action. */
  actions?: SettingsBannerAction[];
  /** When set, the banner gets a dismiss control that calls this. */
  onDismiss?: () => void;
  role?: "status" | "alert";
  className?: string;
};

/**
 * The one banner used above the Settings cards for every multisig notice
 * (invitation, pending request, outcome, unfinished setup).
 *
 * It is built from the same markup as `CardWithToggler` (Card, SdpSettings
 * row, label, Note), so its padding, spacing and type match every card in
 * Settings > General; only the background tone marks it as a notice.
 */
export const SettingsBanner = ({
  variant,
  title,
  children,
  actions,
  onDismiss,
  role = "status",
  className,
}: SettingsBannerProps) => (
  <div
    className={["SettingsBanner", `SettingsBanner--${variant}`, className]
      .filter(Boolean)
      .join(" ")}
    role={role}
  >
    <Card>
      <div className="CardStack__card">
        <div className="SdpSettings">
          <div className="SdpSettings__row">
            <div className="SdpSettings__item">
              <div className="SdpSettings__label">{title}</div>
              {(actions && actions.length > 0) || onDismiss ? (
                <div className="SettingsBanner__controls">
                  {actions?.map((action) => (
                    <Button
                      key={action.label}
                      variant={action.variant ?? "primary"}
                      size="sm"
                      onClick={action.onClick}
                    >
                      {action.label}
                    </Button>
                  ))}
                  {onDismiss ? (
                    <button
                      type="button"
                      className="SettingsBanner__dismiss"
                      aria-label="Dismiss notification"
                      onClick={onDismiss}
                    >
                      <Icon.XClose />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <div className="Note" style={{ width: "75%" }}>
              {children}
            </div>
          </div>
        </div>
      </div>
    </Card>
  </div>
);
